import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { unwrapExtraction } from "@/shared/utils/unwrapExtraction";
import { isExtractionPending } from "@/shared/utils/extractionStatus";

interface Executor {
  name?: string;
  is_primary?: boolean;
  order?: number;
  role?: string;
  relationship?: string;
}

export interface WillExtractionData {
  documentId: number;
  specific: Record<string, unknown> | null;
  status: string | null;
}

export function useWillExtraction(estateId: number | undefined) {
  return useQuery({
    queryKey: ["willExtraction", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_documents")
        .select("id, specific_doc_extraction, extraction_status")
        .eq("estate_id", estateId!)
        .eq("document_type", "will")
        .order("created_at", { ascending: false })
        .limit(1);
      if (!data || data.length === 0) return null;
      const status = data[0].extraction_status as string | null;
      const specific = unwrapExtraction(
        data[0].specific_doc_extraction as Record<string, unknown> | null,
      );
      return { documentId: data[0].id as number, specific, status } as WillExtractionData;
    },
    enabled: !!estateId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && isExtractionPending(d.status) ? 3000 : false;
    },
  });
}

export function checkIsUserExecutor(executorName: string, userName: string): boolean {
  if (!executorName.trim() || !userName.trim()) return false;
  const executorTokens = executorName.toLowerCase().trim().split(/\s+/);
  const userTokens = userName.toLowerCase().trim().split(/\s+/);
  return userTokens.every((token) => executorTokens.includes(token));
}

export function getExecutorsFromExtraction(specific: Record<string, unknown> | null): {
  primary: Executor | undefined;
  successor: Executor | undefined;
} {
  if (!specific) return { primary: undefined, successor: undefined };
  const executors = (specific.executors ?? []) as Executor[];
  const primary = executors.find((e) => e.is_primary) ?? executors[0];
  const successor = executors.find((e) => !e.is_primary && e.order === 2) ?? executors[1];
  return { primary, successor };
}

function resolveStringValue(value: unknown): string {
  if (value == null) {
    return "";
  } else if (typeof value === "string") {
    return value.trim();
  } else if (typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const name = typeof (obj.name ?? obj.full_name) === "string" ? String(obj.name ?? obj.full_name).trim() : "";
    const firm = typeof obj.firm === "string" ? obj.firm.trim() : "";
    if (name && firm) return `${name}, ${firm}`;
    if (name) return name;
    if (firm) return firm;
    return "";
  } else {
    return String(value).trim();
  }
}

export function findExtractionValue(
  specific: Record<string, unknown>,
  keys: string[],
  executors?: { primary: Executor | undefined; successor: Executor | undefined },
): string {
  for (const key of keys) {
    if (key === "__executor_primary") return executors?.primary?.name ?? "";
    if (key === "__executor_successor") return executors?.successor?.name ?? "";

    const directValue = resolveStringValue(specific[key]);
    if (directValue) return directValue;

    for (const val of Object.values(specific)) {
      if (val && typeof val === "object" && !Array.isArray(val)) {
        const nested = val as Record<string, unknown>;
        const nestedValue = resolveStringValue(nested[key]);
        if (nestedValue) return nestedValue;
      }
    }
  }
  return "";
}
